/**
 * The single place the app reads build-time environment configuration.
 *
 * 🛑 EVERY `import.meta.env.REACT_APP_*` READ IN `src/` BELONGS HERE. Nowhere else.
 * Enforced by `tests/noRawEnvReads.test.js`, not by discipline.
 *
 * Why one file: these values are not read at runtime. Vite performs a SUBSTITUTION at build time,
 * replacing each `import.meta.env.REACT_APP_X` LITERAL with the value present when the build
 * started. Two consequences that drive this module's shape:
 *
 *   1. 🛑 Every key is spelled out longhand rather than looped over, and that is now load-bearing
 *      rather than merely tidy. Vite's replacement is PER-LITERAL, so a computed read
 *      (`import.meta.env[name]`) yields `undefined` with no build error and no test failure. Under
 *      CRA the same code happened to work, because DefinePlugin substituted the whole `process.env`
 *      OBJECT — which is exactly why this rule was written down before the cutover rather than
 *      after it. Do not "tidy" this into a loop or a helper that takes the variable name as an
 *      argument.
 *   2. Keeping every literal here makes any future rename one file rather than a hunt across the
 *      tree.
 *
 * ⚠️ The names are still `REACT_APP_*`, not `VITE_*`, and that is deliberate. `vite.config.mjs` sets
 * `envPrefix: 'REACT_APP_'` so Vite exposes the pipeline's existing variables unchanged. A rename
 * would mean editing the `env:` block of `azure-static-web-apps-red-hill-09d4fe100.yml` in the same
 * PR — and this file reads NINE names while that YAML defines SEVEN, so a rename driven off the YAML
 * leaves two silently reading `undefined`. See docs/cloud-audit.md §5.
 *
 * ⚠️ Every value is a GETTER, not a snapshot taken at import. Under the bundler this makes no
 * difference — the literal inside the getter body is substituted just the same. It matters under
 * Vitest, where `import.meta.env` stays a real, mutable object: a test stubs a variable and then
 * calls the code under test, which is only observable if the read happens per access. Snapshotting
 * into a `const` at module load silently breaks those tests.
 *
 * ⚠️ The variables are injected by the DEPLOY PIPELINE (`azure-static-web-apps-red-hill-*.yml`),
 * not a committed `.env`, and they are declared per-branch. Renaming a key here without renaming it
 * in that YAML makes it read `undefined` in the built app. For most keys that degrades a feature;
 * for the two Kinde auth switches it silently reverts sign-in to Firebase, because "absent" is
 * exactly how the documented rollback is performed. Change both halves in the same PR.
 *
 * 🛑 An UNRESOLVED Azure macro is TRUTHY. The pipeline passes `REACT_APP_ENV: $(REACT_APP_ENV)` on
 * every branch but only defines the variable inside the `dev` / `stg` / `prd` conditionals, so a
 * build from any other branch injects the literal string `"$(REACT_APP_ENV)"` — which sails past
 * `|| 'production'` below. That is deliberate for the Kinde switches (`flag()` compares against the
 * exact string `'true'`, so the literal reads as OFF, which is the safe direction), but it means
 * `appEnv` can be a macro rather than an environment name, and `firebase.config.js` then selects
 * devConfig by falling through its `=== 'production'` check. Add a branch to the YAML before
 * building this app from one.
 */

/** A pipeline-injected flag is the STRING 'true', never a boolean. */
const flag = (value) => value === 'true';

/** Strip a scheme and any trailing slashes so a value is safe to compose into a URL. */
const bareDomain = (value, fallback) => (value || fallback || '').replace(/^https?:\/\//, '').replace(/\/+$/, '');

/**
 * `pass.zezamii.com` (SINGULAR) is the RETIRED legacy Vercel deployment. It still answers 200, which
 * is exactly what makes it dangerous — it resolves against the old Vercel data store, so it serves
 * STALE data with no error. Measured 2026-08-27 on Griffith's live resource: the legacy host showed
 * Entry Pass $20.00 / Camping $30.00 while the platform's real configured prices are $32.50 /
 * $42.50. A guest QR pointed there buys at the wrong price against records this platform cannot see.
 *
 * The live guest app is `passes.zezamii.com` (PLURAL) — the Azure App Service that carries
 * BOOKING_PLATFORM_URL/BOOKING_SVC_TOKEN_PUBLIC and therefore actually resolves
 * `/p/:org/:site/:slug` against /v5/booking-public. ONE app serves BOTH booking modes; it picks the
 * flow from the resource's own BookingMode. There is no separate "bookings" host to point at.
 *
 * 🛑 STANDING RULE (Glenn, 2026-08-27): never point at the Vercel host again — it is being retired.
 * The env vars below exist so the host can be MOVED, not so it can be pointed backwards; this guard
 * makes that intent enforced rather than merely documented, since these URLs end up on printed QR
 * codes where a mistake is not recallable.
 */
const RETIRED_GUEST_HOSTS = ['pass.zezamii.com'];
const rejectRetiredHost = (domain) => {
	if (RETIRED_GUEST_HOSTS.includes(domain)) {
		// Fail loudly in dev/CI; in production fall back to the live host rather than emitting a
		// link that silently transacts against retired data.
		const message =
			`[env] Refusing the retired guest host "${domain}". It serves stale legacy Vercel data. ` +
			`Use passes.zezamii.com (the live Azure app, which serves BOTH booking modes).`;
		if (import.meta.env.DEV) throw new Error(message);
		console.error(message);
		return 'passes.zezamii.com';
	}
	return domain;
};

export const env = {
	/** 'development' | 'staging' | 'production'. Absent ⇒ production, matching firebase.config. */
	get appEnv() {
		return import.meta.env.REACT_APP_ENV || 'production';
	},
	get isStaging() {
		// Derived, not a second read: the `|| 'production'` fallback in `appEnv` can never be
		// 'staging', so this is exactly equivalent — and it is one fewer literal to rename if the VITE_ rename ever happens.
		return this.appEnv === 'staging';
	},

	kinde: {
		/** Kinde is AVAILABLE as an auth provider. */
		get enabled() {
			return flag(import.meta.env.REACT_APP_KINDE_AUTH);
		},
		/** Kinde is the PRIMARY front door. Distinct from `enabled` — available ≠ default. */
		get primary() {
			// Reuses `enabled` rather than repeating the REACT_APP_KINDE_AUTH literal. These two are
			// the pair whose half-missed rename silently reverts sign-in to Firebase, so the fewer
			// places the cutover has to touch them, the better.
			return this.enabled && flag(import.meta.env.REACT_APP_KINDE_PRIMARY);
		},
		get clientId() {
			return import.meta.env.REACT_APP_KINDE_CLIENT_ID;
		},
		get domain() {
			return import.meta.env.REACT_APP_KINDE_DOMAIN;
		},
		get audience() {
			return import.meta.env.REACT_APP_KINDE_AUDIENCE;
		},

		/**
		 * The Kinde tenant to use for a given backend `environment`.
		 *
		 * ⭐ WHY THIS EXISTS. Staging carries an API/Firebase switch (`selectedEnvironment`,
		 * Development ↔ Production) so stg's UI can be exercised against either backend. Two of the
		 * three things that must agree already follow it at RUNTIME — the API base (superFetch,
		 * socketURLGenerator) and the Firebase project (firebase.config). The Kinde tenant did not:
		 * `REACT_APP_KINDE_*` is inlined at BUILD time, so it was pinned to dev whichever way the
		 * switch was thrown.
		 *
		 * That half-coherence is a real defect, not a theoretical one. Measured 2026-08-27: stg with
		 * the switch on Production sent a DEV-tenant Kinde token to df.api, whose issuer allow-list
		 * holds auth.zezamii.com only. It fell through to the Firebase verifier and surfaced as
		 * `Firebase ID token has incorrect "aud" claim. Expected "zezamii-3f2a8"` — an error naming
		 * neither Kinde nor the real cause. The switch worked correctly in the Firebase era (both
		 * halves followed it); the Kinde cutover on 2026-08-25 is what broke it, two days earlier.
		 *
		 * 🛑 STAGING ONLY, deliberately. Every other build has exactly ONE tenant and must resolve to
		 * it unconditionally — a dev or prd build that could be talked into another tenant by a
		 * localStorage value would be a far worse bug than the one this fixes. The `isStaging` guard
		 * is the load-bearing part of this function; do not "simplify" it away.
		 *
		 * Falls back to the single configured tenant whenever the alternate is absent, so a stg build
		 * that has not yet had the PROD vars added behaves exactly as it does today.
		 *
		 * @param {string} environment 'development' | 'staging' | 'production' — the BACKEND target
		 * @returns {{clientId: string, domain: string, audience: string}}
		 */
		forEnvironment(environment) {
			const configuredTenant = { clientId: this.clientId, domain: this.domain, audience: this.audience };
			// Validate by shape, not truthiness: an undefined pipeline var arrives as the literal "$(REACT_APP_…)".
			// All three or none — a partial tenant cannot complete a login.
			const urlOk = (v) => typeof v === 'string' && v.startsWith('https://');
			const idOk = (v) => typeof v === 'string' && /^[a-f0-9]{32}$/i.test(v);
			const isCompleteTenant = (tenant) =>
				urlOk(tenant.domain) && urlOk(tenant.audience) && idOk(tenant.clientId);

			// Local dev server only (MODE is 'production' in builds, 'test' under vitest): the tenant
			// follows the backend environment from the per-tenant REACT_APP_{DEV,PROD}_KINDE_* in .env.
			if (import.meta.env.MODE === 'development') {
				const localTenant =
					environment === 'production'
						? {
								clientId: import.meta.env.REACT_APP_PROD_KINDE_CLIENT_ID,
								domain: import.meta.env.REACT_APP_PROD_KINDE_DOMAIN,
								audience: import.meta.env.REACT_APP_PROD_KINDE_AUDIENCE
							}
						: {
								clientId: import.meta.env.REACT_APP_DEV_KINDE_CLIENT_ID,
								domain: import.meta.env.REACT_APP_DEV_KINDE_DOMAIN,
								audience: import.meta.env.REACT_APP_DEV_KINDE_AUDIENCE
							};
				return isCompleteTenant(localTenant) ? localTenant : configuredTenant;
			}

			if (!env.isStaging || environment !== 'production') return configuredTenant;
			const stagingProdTenant = {
				clientId: import.meta.env.REACT_APP_KINDE_PROD_CLIENT_ID,
				domain: import.meta.env.REACT_APP_KINDE_PROD_DOMAIN,
				audience: import.meta.env.REACT_APP_KINDE_PROD_AUDIENCE
			};
			return isCompleteTenant(stagingProdTenant) ? stagingProdTenant : configuredTenant;
		}
	},

	/** Local App Server port for the dev server; lets a session target its own backend. Default 8080. */
	get localApiPort() {
		return import.meta.env.REACT_APP_LOCAL_API_PORT || '8080';
	},

	/** Publishable (not secret) Stripe key. Trimmed — a pipeline value can carry stray whitespace. */
	get stripePublishableKey() {
		return (import.meta.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
	},

	/** Guest booking portal host, no scheme or trailing slash. */
	get passPortalDomain() {
		return rejectRetiredHost(bareDomain(import.meta.env.REACT_APP_PASS_PORTAL_URL, 'passes.zezamii.com'));
	},
	/** Day-pass guest portal host — same app, separate knob so the two can diverge. */
	get dayPassDomain() {
		return rejectRetiredHost(bareDomain(import.meta.env.REACT_APP_DAY_PASS_URL, 'passes.zezamii.com'));
	}
};
